import { Component, computed, input, signal } from '@angular/core';
import { FormField, form, maxLength, minLength, pattern, required, schema, validate } from '@angular/forms/signals';
import { NotificationService, TelemetryUnitService } from '@app/services';

import {
    IonButton,
    IonButtons,
    IonHeader,
    IonInput,
    IonItem,
    IonTitle,
    IonToolbar,
    ModalController,
    IonIcon,
    IonNote,
    IonList
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { TranslateModule } from '@ngx-translate/core';


@Component({
    selector: 'app-change-telemetry-unit-pin-modal',
    templateUrl: 'change-telemetry-unit-pin-modal.component.html',
    styleUrls: ['change-telemetry-unit-pin-modal.component.scss'],
    imports: [
        FormField,
        IonButton,
        IonButtons,
        IonHeader,
        IonItem,
        IonList,
        IonTitle,
        IonToolbar,
        IonInput,
        IonIcon,
        IonNote,
        TranslateModule
    ],
})
export class ChangeTelemetryUnitPinModalComponent {
    telemetryUnitId = input('');
    readonly pinFormModel = signal<ChangeTelemetryUnitPinFormModel>({
        oldPin: '',
        newPin: '',
        confirmNewPin: ''
    });
    readonly pinForm = form(this.pinFormModel, pinFormSchema);
    readonly hasPinMismatch = computed(() =>
        !!this.pinForm.confirmNewPin().value() && this.pinForm.newPin().value() !== this.pinForm.confirmNewPin().value()
    );
    readonly isSubmitDisabled = computed(() =>
        this.pinForm().invalid()
    );

    constructor(
        private readonly modalCtrl: ModalController,
        private readonly telemetryUnitService: TelemetryUnitService,
        private readonly notificationService: NotificationService
    ) {
        addIcons({ close });
    }

    cancel() {
        return this.modalCtrl.dismiss(null, 'cancel');
    }

    confirm() {
        if (this.isSubmitDisabled()) {
            return;
        }

        return this.modalCtrl.dismiss({
            oldPin: this.pinForm.oldPin().value(),
            newPin: this.pinForm.newPin().value()
        }, 'confirm');
    }

    onSubmit() {
        if (this.isSubmitDisabled() || !this.telemetryUnitId) {
            return;
        }

        this.telemetryUnitService.updateTelemetryUnitPin(
            this.telemetryUnitId(),
            this.pinForm.oldPin().value(),
            this.pinForm.newPin().value()
        ).subscribe({
            next: () => {
                this.notificationService.notifySavedSuccessfully();
                this.modalCtrl.dismiss({
                    oldPin: this.pinForm.oldPin().value(),
                    newPin: this.pinForm.newPin().value()
                }, 'confirm');
            },
            error: error => {
                if (error?.status === 403) {
                    this.notificationService.notifyErrorWithTranslate(
                        'invalid-pin-code',
                        'changeTelemetryUnitPinModal.updateForbiddenMessage'
                    );
                }
                else {
                    this.notificationService.notifyErrorWithTranslate('Error','CheckInputAndTryAgainLater');
                }

                this.modalCtrl.dismiss(null, 'error');
            }
        });
    }
}

interface ChangeTelemetryUnitPinFormModel {
    oldPin: string;
    newPin: string;
    confirmNewPin: string;
}

const pinFormSchema = schema<ChangeTelemetryUnitPinFormModel>(path => {
    required(path.oldPin, { message: 'changeTelemetryUnitPinModal.currentPinRequiredError' });
    required(path.newPin, { message: 'changeTelemetryUnitPinModal.newPinRequiredError' });
    required(path.confirmNewPin, { message: 'changeTelemetryUnitPinModal.confirmNewPinRequiredError' });
    minLength(path.newPin, 4, { message: 'changeTelemetryUnitPinModal.newPinMinLengthError' });
    maxLength(path.newPin, 4, { message: 'changeTelemetryUnitPinModal.newPinMaxLengthError' });
    pattern(path.newPin, /^\d*$/, { message: 'changeTelemetryUnitPinModal.newPinPatternError' });
    validate(path.confirmNewPin, ({ value, valueOf }) => {
        const pinConfirmation = value();
        if (pinConfirmation && pinConfirmation !== valueOf(path.newPin)) {
            return {
                kind: 'mismatch',
                message: 'changeTelemetryUnitPinModal.confirmNewPinMismatchError'
            }
        }
        return null;
    });
});